/**
 * BrandWordmark — "NU Assist." with the accent period + stage badge.
 *
 * v35 `.app-title` + `.accent-dot`: 800-weight wordmark, tight tracking,
 * the trailing dot in accent. Rendered with token utilities only — the
 * `text-h2` role (15px) sized up via `text-h1` is overkill for a header
 * line, so we use `text-h3` (14px) with an explicit `font-extrabold`
 * weight override (v35's `font-weight:800`, the design-spec allowance
 * for per-spot weight overrides).
 *
 * 2026-06-12: appended a `<StageBadge>` next to the wordmark. The
 * canonical stage label is resolved via `getStageLabel()` from
 * `@nu-assist/shared-fe` so web + extension show the same stage
 * affordance (green LOCAL / orange DEV / silent on prod). The badge
 * renders `null` on prod, so the prod header output is byte-identical
 * to before this change.
 *
 * 2026-09-21: in the light theme the header is the brand emerald
 * (`tokens.css`, the brand-header token scope). The wordmark follows through
 * its tokens; the badge cannot, because `StageBadge` paints Tailwind palette
 * colours, and its green label on a 15% green tint is green on green there.
 * So on light it is repainted as a Tide 2 pair, deep ink on the family's
 * opaque pastel, through the web-only `light:` variant. `StageBadge` itself
 * is untouched: its other consumers are all in the extension, which has no
 * light theme, and dark keeps the palette classes.
 *
 * 2026-10-08: the white NU Advisory mark (`/nu-mark-white.png`, 66 px tall,
 * drawn at `h-6` = 24 px: the spacing scale, never an arbitrary px value)
 * leads the wordmark. White reads on both headers: the
 * emerald one in light and the black one in dark. Decorative (`alt=""`):
 * the home button that wraps this already carries the label.
 *
 * 2026-10-27: a personal stage's build (`VITE_PERSONAL_STAGE`, e.g. `jl-dev`)
 * names its owner in the badge, `JL-DEV`, so two devs' tabs are never
 * confused with each other or with the shared dev. Shared dev, local and prod
 * are unchanged.
 */
import { StageBadge } from "@nu-assist/ui";
import { getStageLabel } from "@nu-assist/shared-fe";

/** Exported for `tests/tailwind.config.test.ts`, which compiles these exact
 *  classes: a `light:` class that emits nothing fails no DOM test. */
export const LIGHT_STAGE_BADGE = {
  local: "light:bg-success-muted light:border-success-muted light:text-success",
  dev: "light:bg-conflict-muted light:border-conflict-muted light:text-conflict",
  prod: undefined,
} as const;

/** `jl-dev` → `JL-DEV`; `undefined` (shared dev, local, prod) keeps the default label. */
export function personalStageLabel(personalStage: string | undefined): string | undefined {
  const prefix = personalStage?.replace(/-dev$/, "");
  return prefix ? `${prefix.toUpperCase()}-DEV` : undefined;
}

export function BrandWordmark() {
  const { stage } = getStageLabel();
  const label =
    stage === "dev" ? personalStageLabel(import.meta.env.VITE_PERSONAL_STAGE) : undefined;
  return (
    <span className="inline-flex items-center gap-2">
      <img
        src="/nu-mark-white.png"
        alt=""
        data-testid="brand-nu-mark"
        className="h-6 w-auto shrink-0 select-none"
      />
      <span className="text-h3 font-extrabold tracking-tight text-text select-none">
        NU Assist<span className="text-accent">.</span>
      </span>
      <StageBadge
        stage={stage}
        label={label}
        className={LIGHT_STAGE_BADGE[stage]}
        data-testid="brand-stage-badge"
      />
    </span>
  );
}
